/**
 * Кабинет поставщика (ВК-06, ЗК-18, ЗК-19): ответ по форме или загрузкой Excel/PDF.
 */
import {
  type FileInfo,
  RFQ_OFFER_FILE_STATUSES,
  RFQ_STATUSES,
  type SupplierRfqInviteDetail,
  type SupplierRfqInviteSummary,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Paperclip } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FileLink } from '../../components/FileLink';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';

const listQuery = {
  queryKey: ['procurement', 'cabinet', 'rfqs'],
  queryFn: () => api<SupplierRfqInviteSummary[]>('/procurement/cabinet/rfqs'),
};
const detailQuery = (id: string) => ({
  queryKey: ['procurement', 'cabinet', 'rfqs', id],
  queryFn: () => api<SupplierRfqInviteDetail>(`/procurement/cabinet/rfqs/${id}`),
});

export function SupplierCabinetPage() {
  const me = useMe();
  const q = useQuery({ ...listQuery, enabled: Boolean(me.tenant?.counterpartyId) });
  useCrumbs([{ label: 'Запросы КП' }], q.error);
  if (!me.tenant?.counterpartyId || me.tenant.staff) {
    return (
      <Page>
        <EmptyState title="Кабинет поставщика">
          Этот раздел открыт внешнему поставщику, привязанному к организации в реестре контрагентов.
        </EmptyState>
      </Page>
    );
  }
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const open = q.data.filter((r) => r.open && !r.respondedAt && !r.declinedAt);
  const done = q.data.filter((r) => r.respondedAt || r.declinedAt || !r.open);
  return (
    <Page>
      <PageHeader
        title="Кабинет поставщика"
        subtitle="Запросы коммерческих предложений, на которые вас пригласили"
      />
      {!q.data.length ? (
        <EmptyState title="Пока нет запросов">
          Когда снабженец разошлёт вам запрос КП, он появится здесь.
        </EmptyState>
      ) : (
        <div className="flex flex-col gap-4">
          <Panel title="Ждут ответа" meta={String(open.length)}>
            {!open.length ? (
              <p className="px-5 py-4 text-[14px] text-ink-2">Открытых запросов нет.</p>
            ) : (
              <ul className="divide-y divide-line">
                {open.map((r) => (
                  <InviteRow key={r.id} r={r} />
                ))}
              </ul>
            )}
          </Panel>
          {done.length ? (
            <Panel title="Уже ответили или закрыты" meta={String(done.length)}>
              <ul className="divide-y divide-line">
                {done.map((r) => (
                  <InviteRow key={r.id} r={r} />
                ))}
              </ul>
            </Panel>
          ) : null}
        </div>
      )}
    </Page>
  );
}

function InviteRow({ r }: { r: SupplierRfqInviteSummary }) {
  return (
    <li>
      <Link
        to="/suppliers/cabinet/rfqs/$rfqId"
        params={{ rfqId: r.id }}
        className="flex min-h-11 flex-col gap-0.5 px-5 py-3 hover:bg-tile/60 sm:flex-row sm:items-center sm:justify-between"
      >
        <span>
          <span className="font-medium text-ink">{r.number}</span> <span className="text-ink">{r.title}</span>
          <span className="mt-0.5 block text-[13px] text-ink-2">
            {r.lines} поз.
            {r.responseDue ? ` · ответ до ${date(r.responseDue)}` : ''}
          </span>
        </span>
        <Badge tone={r.declinedAt ? 'danger' : r.respondedAt ? 'success' : r.overdue ? 'warning' : 'info'}>
          {r.declinedAt
            ? 'отказ'
            : r.respondedAt
              ? 'отвечено'
              : r.overdue
                ? 'срок прошёл'
                : RFQ_STATUSES[r.status]}
        </Badge>
      </Link>
    </li>
  );
}

export function SupplierRfqReplyPage() {
  const { rfqId } = useParams({ strict: false }) as { rfqId: string };
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ ...detailQuery(rfqId), enabled: Boolean(me.tenant?.counterpartyId) });
  useCrumbs(
    q.data
      ? [
          { label: 'Кабинет поставщика', to: '/suppliers/cabinet' },
          { label: q.data.number, title: q.data.title },
        ]
      : null,
    q.error,
  );

  const [prices, setPrices] = useState<Record<string, string>>({});
  const [deliveryDays, setDeliveryDays] = useState('');
  const [deliveryIncluded, setDeliveryIncluded] = useState(false);
  const [deliveryCost, setDeliveryCost] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [note, setNote] = useState('');
  const [validUntil, setValidUntil] = useState('');

  useEffect(() => {
    if (!q.data) return;
    const next: Record<string, string> = {};
    for (const l of q.data.lineList) next[l.id] = l.price != null ? String(l.price) : '';
    setPrices(next);
    const p = q.data.previous;
    setDeliveryDays(p?.deliveryDays != null ? String(p.deliveryDays) : '');
    setDeliveryIncluded(p?.deliveryIncluded ?? false);
    setDeliveryCost(p?.deliveryCost != null ? String(p.deliveryCost) : '');
    setPaymentTerms(p?.paymentTerms ?? '');
    setNote(p?.note ?? '');
    setValidUntil(p?.validUntil ?? '');
  }, [q.data]);

  const reply = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        prices: Object.fromEntries(
          Object.entries(prices).map(([id, v]) => {
            const t = v.trim().replace(/\s/g, '').replace(',', '.');
            return [id, t === '' ? null : Number(t)];
          }),
        ),
        deliveryIncluded,
        deliveryDays: deliveryDays.trim() === '' ? null : Number(deliveryDays),
        deliveryCost:
          deliveryCost.trim() === '' ? null : Number(deliveryCost.replace(/\s/g, '').replace(',', '.')),
        paymentTerms: paymentTerms.trim() || null,
        note: note.trim() || null,
        validUntil: validUntil || null,
      };
      return api<SupplierRfqInviteDetail>(`/procurement/cabinet/rfqs/${rfqId}/reply`, { body });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['procurement', 'cabinet', 'rfqs'] });
      qc.setQueryData(['procurement', 'cabinet', 'rfqs', rfqId], r);
      toast('Ответ отправлен');
    },
  });
  const decline = useMutation({
    mutationFn: () =>
      api<SupplierRfqInviteDetail>(`/procurement/cabinet/rfqs/${rfqId}/decline`, { body: {} }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['procurement', 'cabinet', 'rfqs'] });
      qc.setQueryData(['procurement', 'cabinet', 'rfqs', rfqId], r);
      toast('Отказ записан');
    },
  });
  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const uploaded = await api<FileInfo>('/files', { form, method: 'POST' });
      return api<SupplierRfqInviteDetail>(`/procurement/cabinet/rfqs/${rfqId}/offer-files`, {
        body: { fileId: uploaded.id },
      });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['procurement', 'cabinet', 'rfqs'] });
      qc.setQueryData(['procurement', 'cabinet', 'rfqs', rfqId], r);
      toast('Файл принят как ответ');
    },
  });

  if (!me.tenant?.counterpartyId || me.tenant.staff) {
    return (
      <Page>
        <EmptyState title="Нет доступа">Ответ на запрос КП доступен только поставщику.</EmptyState>
      </Page>
    );
  }
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const r = q.data;
  const locked = !r.open || r.stopListed;
  return (
    <Page>
      <PageHeader
        title={`${r.number} ${r.title}`}
        subtitle={`${RFQ_STATUSES[r.status]}${r.responseDue ? ` · ответ до ${date(r.responseDue)}` : ''}`}
        actions={
          <Button onClick={() => void nav({ to: '/suppliers/cabinet' })} className="min-h-11">
            К списку
          </Button>
        }
      />
      {r.stopListed ? (
        <p className="mb-3 rounded-md border border-danger/40 bg-danger-soft px-4 py-3 text-[14px] text-danger">
          Ваша организация в стоп-листе или со «стопом» проверки: ответ не принимается.
        </p>
      ) : null}
      {reply.error ? <ErrorBox error={reply.error} className="mb-3" /> : null}
      {decline.error ? <ErrorBox error={decline.error} className="mb-3" /> : null}
      {uploadFile.error ? <ErrorBox error={uploadFile.error} className="mb-3" /> : null}

      <Panel
        title="Ответ файлом (Excel или PDF)"
        meta="ЗК-19"
        className="mb-4"
        actions={
          r.open && !r.stopListed ? (
            <label className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md border border-line px-3 text-[13px] font-medium text-ink hover:bg-tile">
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="sr-only"
                disabled={uploadFile.isPending}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadFile.mutate(f);
                  e.target.value = '';
                }}
              />
              <Paperclip className="size-4" aria-hidden />
              {uploadFile.isPending ? 'Загрузка…' : 'Загрузить файл'}
            </label>
          ) : null
        }
      >
        <p className="border-b border-line px-5 py-3 text-[13px] text-ink-2">
          Excel с колонками «код», «название», «цена», «срок» раскладывается по позициям запроса. PDF и скан —
          как вложение на проверку снабженцем.
        </p>
        {r.offerFiles?.length ? (
          <ul className="divide-y divide-line text-[14px]">
            {r.offerFiles.map((f) => (
              <li
                key={f.id}
                className="flex flex-col gap-1 px-5 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <FileLink file={{ id: f.fileId, name: f.fileName, scanState: 'clean' }} />
                <Badge
                  tone={
                    f.parseStatus === 'confirmed'
                      ? 'success'
                      : f.parseStatus === 'failed'
                        ? 'danger'
                        : 'warning'
                  }
                >
                  {RFQ_OFFER_FILE_STATUSES[f.parseStatus]}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-4 text-[14px] text-ink-2">Файлов ответа пока нет.</p>
        )}
      </Panel>

      <Panel title="Позиции запроса">
        <ul className="divide-y divide-line px-0 text-[14px]">
          {r.lineList.map((l) => (
            <li
              key={l.id}
              className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-end sm:justify-between"
            >
              <div>
                <div className="font-medium text-ink">{l.name}</div>
                <div className="text-[13px] text-ink-2">
                  {l.quantity} {l.unit}
                </div>
              </div>
              <Field label="Цена за ед. с НДС, ₸" className="w-full sm:w-44">
                <Input
                  inputMode="decimal"
                  value={prices[l.id] ?? ''}
                  disabled={locked}
                  onChange={(e) => setPrices((p) => ({ ...p, [l.id]: e.target.value }))}
                  aria-label={`Цена ${l.name}`}
                  className="min-h-11"
                />
              </Field>
            </li>
          ))}
        </ul>
        {r.deliveryAddress ? (
          <p className="border-t border-line px-5 py-3 text-[14px]">Доставка: {r.deliveryAddress}</p>
        ) : null}
        {r.requirements ? (
          <p className="border-t border-line px-5 py-3 text-[14px]">Требования: {r.requirements}</p>
        ) : null}
      </Panel>

      <Panel title="Условия" className="mt-4">
        <div className="flex flex-col gap-4 p-5">
          <Field label="Срок поставки, дней">
            <Input
              inputMode="numeric"
              value={deliveryDays}
              disabled={locked}
              onChange={(e) => setDeliveryDays(e.target.value)}
              className="min-h-11"
            />
          </Field>
          <Checkbox
            className="min-h-11 items-center"
            label="Доставка включена в цену"
            checked={deliveryIncluded}
            disabled={locked}
            onChange={(e) => setDeliveryIncluded(e.target.checked)}
          />
          {!deliveryIncluded ? (
            <Field label="Стоимость доставки, ₸">
              <Input
                inputMode="decimal"
                value={deliveryCost}
                disabled={locked}
                onChange={(e) => setDeliveryCost(e.target.value)}
                className="min-h-11"
              />
            </Field>
          ) : null}
          <Field label="Условия оплаты">
            <Input
              value={paymentTerms}
              disabled={locked}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="min-h-11"
            />
          </Field>
          <Field label="КП действует до">
            <Input
              type="date"
              value={validUntil}
              disabled={locked}
              onChange={(e) => setValidUntil(e.target.value)}
              className="min-h-11"
            />
          </Field>
          <Field label="Комментарий">
            <Textarea value={note} disabled={locked} onChange={(e) => setNote(e.target.value)} rows={3} />
          </Field>
        </div>
      </Panel>

      {r.messages.length ? (
        <Panel title="Переписка по запросу" className="mt-4">
          <ul className="divide-y divide-line text-[14px]">
            {r.messages.map((m) => (
              <li key={m.id} className="px-5 py-3">
                <div className="text-[12px] text-ink-2">{dateTime(m.at)}</div>
                {m.body}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {r.open && !r.stopListed ? (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <Button
            variant="primary"
            className="min-h-11 w-full sm:w-auto"
            loading={reply.isPending}
            onClick={() => reply.mutate()}
          >
            {r.respondedAt ? 'Обновить ответ' : 'Отправить КП'}
          </Button>
          {!r.respondedAt ? (
            <Button
              variant="danger"
              className="min-h-11 w-full sm:w-auto"
              loading={decline.isPending}
              onClick={() => {
                if (window.confirm('Отказаться отвечать на этот запрос?')) decline.mutate();
              }}
            >
              Отказаться
            </Button>
          ) : null}
        </div>
      ) : r.declinedAt ? (
        <p className="mt-4 text-[14px] text-ink-2">Вы отказались отвечать на этот запрос.</p>
      ) : r.respondedAt ? (
        <p className="mt-4 text-[14px] text-ink-2">
          Ответ принят. Снабженец видит его в заявке и сравнении КП.
        </p>
      ) : null}
    </Page>
  );
}
